import React, { ReactNode, CSSProperties } from 'react'
import styled from 'styled-components'
import { transparentize } from 'polished'

import Heading from 'components/Heading'
import { Flex, Text } from 'components/Box'

const iconSizeToPixels = {
  small: 42,
  medium: 64,
  large: 72,
} as const

type IconSize = keyof typeof iconSizeToPixels

export const Container = styled(Flex).attrs({
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
})`
  width: 100%;
  height: 100%;
  flex: 1 0 0;
`

interface TextProps {
  dark?: boolean
}

export const Title = styled(Heading.H3)<TextProps>`
  max-width: 100%;
  text-align: center;

  margin-top: 20px;
  margin-bottom: 6px; /* stylelint-disable-line scales/space */

  font-size: 1rem;
  line-height: 1;

  color: ${({ theme, dark }) =>
    transparentize(1 - 0.75, dark ? theme.colors.white : theme.colors.black)};
`

export const Body = styled(Text)<TextProps>`
  max-width: 100%;
  text-align: center;

  margin-top: 0;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  font-size: 0.875rem;
  line-height: 1.5;
  color: ${({ theme, dark }) =>
    transparentize(1 - 0.5, dark ? theme.colors.white : theme.colors.black)};
`

interface IconContainerProps {
  iconSizePx: number
  dark?: boolean
}

const IconContainer = styled.div<IconContainerProps>`
  width: ${props => props.iconSizePx}px;
  height: ${props => props.iconSizePx}px;
  color: ${({ theme, dark }) =>
    dark ? theme.colors.white : theme.colors.black};
`

type IconRenderer = (props: { width?: number; iconSize: IconSize }) => ReactNode

export interface ErrorMessageProps {
  // Icon component or React element
  icon: ReactNode | IconRenderer
  iconSize?: IconSize
  title: string
  // Description of error state
  description?: string | ReactNode
  extra?: ReactNode
  // Dark theme, used for empty states in prototyping
  dark?: boolean
  style?: CSSProperties
}

const ErrorMessage: React.FC<ErrorMessageProps> = props => {
  const {
    children,
    dark,
    description,
    extra,
    icon,
    iconSize = 'large',
    title,
  } = props
  const textContent = children || description
  const iconSizePixels = iconSizeToPixels[iconSize]

  let iconComponent
  if (icon && React.isValidElement(icon)) {
    iconComponent = (
      <IconContainer iconSizePx={iconSizePixels} dark={dark}>
        {icon}
      </IconContainer>
    )
  } else if (icon && typeof icon === 'function') {
    iconComponent = icon({ width: iconSizePixels, iconSize })
  } else {
    iconComponent = null
  }

  return (
    <Container {...props}>
      {iconComponent}
      <Title dark={dark}>{title}</Title>
      {textContent && <Body dark={dark}>{textContent}</Body>}
      {extra}
    </Container>
  )
}

export default ErrorMessage
